import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import {
  Button,
  StandardDialog,
  createCurrencyField,
  createDateField,
  createSelectField,
  createTextField,
} from '@roc/ui';
import { LIEN_TYPE_OPTIONS } from '../../store/lienFormStore';
import { useLienPayoffReviewStore } from '../../hooks/useLienPayoffReviewStore';
import { DateFormat, formatDate } from '@roc/feature-utils';

export interface AddEditLienModalProps {
  open: boolean;
  onClose: () => void;
  editingLienId?: number | null;
}

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: 0,
  },
  closeButton: {
    border: '1px solid #D0D0D0',
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontWeight: 600,
    width: 120,
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
  saveButton: {
    textTransform: 'none',
    fontWeight: 600,
    width: 120,
  },
}));

// Manual Add/Edit lien for the Payoff task - mirrors libs/feature-lien's AddEditLienModal
// (Title Commitment) since both write to the same property_liens table via the same
// backend endpoint (saveLien). No property dropdown: a payoff always belongs to the Payoff task's
// own property. Confirmed as needed: 2026-09-15 planning meeting, item 8.
export const AddEditLienModal = observer(
  ({ open, onClose, editingLienId }: AddEditLienModalProps) => {
    const classes = useStyles();
    const { lienPayoffReviewStore } = useLienPayoffReviewStore();
    const store = lienPayoffReviewStore.lienFormStore;

    useEffect(() => {
      if (open) {
        lienPayoffReviewStore.loadForm(editingLienId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editingLienId]);

    const dialogContent = (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {createSelectField({
            store,
            fieldName: 'lienType',
            testId: 'lienType',
            label: 'Lien Type',
            options: LIEN_TYPE_OPTIONS,
          })}
        </Grid>
        <Grid item xs={6}>
          {createTextField({
            store,
            fieldName: 'madeBy',
            testId: 'madeBy',
            label: 'Borrower',
          })}
        </Grid>
        <Grid item xs={6}>
          {createTextField({
            store,
            fieldName: 'madeTo',
            testId: 'madeTo',
            label: 'Lender',
          })}
        </Grid>
        <Grid item xs={6}>
          {createDateField({
            store,
            fieldName: 'datedDate',
            testId: 'datedDate',
            label: 'Dated',
            disableFuture: true,
            onChange: (name, date) =>
              store.onFieldChange(name, formatDate(date, DateFormat.MMDDYYYY)),
          })}
        </Grid>
        <Grid item xs={6}>
          {createDateField({
            store,
            fieldName: 'recordedDate',
            testId: 'recordedDate',
            label: 'Recorded',
            disableFuture: true,
            onChange: (name, date) =>
              store.onFieldChange(name, formatDate(date, DateFormat.MMDDYYYY)),
          })}
        </Grid>
        <Grid item xs={12}>
          {createCurrencyField({
            store,
            fieldName: 'originalAmount',
            testId: 'originalAmount',
            label: 'Amount',
          })}
        </Grid>
      </Grid>
    );

    const handleSave = async () => {
      await lienPayoffReviewStore.saveLien(editingLienId);
      onClose();
    };

    const dialogActions = (
      <>
        <Button
          className={classes.closeButton}
          variant="outlined"
          onClick={onClose}
          testId="add-edit-payoff-lien-cancel"
        >
          Cancel
        </Button>
        <Button
          className={classes.saveButton}
          variant="contained"
          color="primary"
          onClick={handleSave}
          testId="add-edit-payoff-lien-save"
        >
          Save
        </Button>
      </>
    );

    return (
      <StandardDialog
        open={open}
        title={`${!editingLienId ? 'Add' : 'Edit'} Payoff`}
        maxWidth="sm"
        fullWidth
        handleClose={onClose}
        dialogContent={dialogContent}
        dialogActions={dialogActions}
        dialogContentclasses={{ root: classes.dialogContent }}
      />
    );
  }
);
